import type {
  ClaimRelationshipCandidateResult,
  ClaimRelationshipView,
  CreateClaimRelationshipInput,
} from '@billing/validation';
import {
  claimRelationshipCandidateResultSchema,
  claimRelationshipViewSchema,
} from '@billing/validation';
import { useTranslation } from '@refinedev/core';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { claimQueryKeys } from 'api/resource/claim';
import { deleteRequest, getRequest, postRequest } from 'api/utils/HttpMethods';
import { useMutationQuery } from 'api/utils/queries';

const relationshipPath = '/resource/claim/relationship';

export const claimRelationshipQueryKeys = {
  all: ['claimRelationship'],
  byClaim: (claimID: string) => [
    ...claimRelationshipQueryKeys.all,
    'claim',
    claimID,
  ],
  candidates: (
    claimID: string,
    search: string,
    page: number,
    pageSize: number
  ) => [
    ...claimRelationshipQueryKeys.all,
    'candidates',
    claimID,
    search,
    page,
    pageSize,
  ],
};

const getClaimRelationships = async (
  claimID: string
): Promise<ClaimRelationshipView[]> => {
  const params = new URLSearchParams({ claimID });
  const response = await getRequest<unknown>(
    '',
    `${relationshipPath}?${params.toString()}`
  );
  return claimRelationshipViewSchema.array().parse(response);
};

const getClaimRelationshipCandidates = async ({
  claimID,
  search,
  page,
  pageSize,
}: {
  claimID: string;
  search: string;
  page: number;
  pageSize: number;
}): Promise<ClaimRelationshipCandidateResult> => {
  const params = new URLSearchParams({
    claimID,
    search,
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await getRequest<unknown>(
    '',
    `${relationshipPath}/candidates?${params.toString()}`
  );
  return claimRelationshipCandidateResultSchema.parse(response);
};

const createClaimRelationship = async (
  input: CreateClaimRelationshipInput
): Promise<unknown> => await postRequest('', relationshipPath, input);

export interface DeleteClaimRelationshipVariables {
  relationshipID: string;
  fromClaimID: string;
  toClaimID: string;
}

const deleteClaimRelationship = async ({
  relationshipID,
}: DeleteClaimRelationshipVariables): Promise<unknown> =>
  await deleteRequest('', relationshipPath, { relationshipID });

export const useClaimRelationships = (
  claimID: string,
  enabled = true
): UseQueryResult<ClaimRelationshipView[], Error> =>
  useQuery({
    queryKey: claimRelationshipQueryKeys.byClaim(claimID),
    queryFn: () => getClaimRelationships(claimID),
    enabled: enabled && Boolean(claimID),
    retry: false,
  });

export const useClaimRelationshipCandidates = ({
  claimID,
  search,
  page = 0,
  pageSize = 20,
  enabled = true,
}: {
  claimID: string;
  search: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}): UseQueryResult<ClaimRelationshipCandidateResult, Error> =>
  useQuery({
    queryKey: claimRelationshipQueryKeys.candidates(
      claimID,
      search,
      page,
      pageSize
    ),
    queryFn: () =>
      getClaimRelationshipCandidates({ claimID, search, page, pageSize }),
    enabled: enabled && Boolean(claimID),
    retry: false,
  });

export const useCreateClaimRelationship = () => {
  const { translate } = useTranslation();
  return useMutationQuery<unknown>({
    mutationFn: (input) => createClaimRelationship(input),
    mutationKey: [...claimRelationshipQueryKeys.all, 'create'],
    queryKey: claimRelationshipQueryKeys.all,
    invalidateQueryKeys: [claimRelationshipQueryKeys.all, claimQueryKeys.all],
    successMessage: translate(
      'pages:claims.relationships.notifications.linked',
      'Claims linked'
    ),
    errorMessage: translate(
      'pages:claims.relationships.notifications.linkError',
      'Could not link claims'
    ),
  });
};

export const useDeleteClaimRelationship = () => {
  const { translate } = useTranslation();
  return useMutationQuery<unknown>({
    mutationFn: (input) => deleteClaimRelationship(input),
    mutationKey: [...claimRelationshipQueryKeys.all, 'delete'],
    queryKey: claimRelationshipQueryKeys.all,
    invalidateQueryKeys: [claimRelationshipQueryKeys.all, claimQueryKeys.all],
    successMessage: translate(
      'pages:claims.relationships.notifications.unlinked',
      'Claims unlinked'
    ),
    errorMessage: translate(
      'pages:claims.relationships.notifications.unlinkError',
      'Could not unlink claims'
    ),
  });
};
